<template>
    <div>
        <b-modal
            id="clickDrag"
            ref="clickDrag"
            :lazy="true"
            @hidden="close"
            :scrollable="true"
            title-tag="h2"
            :title="title"
        >
            <div v-if="page === 'appointment'" class="form-horizontal px-2">
                <p v-html="mainDescription"></p>
                <div
                    v-if="isSameTimeAndResource"
                    class="alert alert-warning mb-3"
                >
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    <strong>Cannot reschedule appointment:</strong> The selected
                    time is the same as the current appointment time. This may
                    be due to daylight saving time adjustments or an invalid
                    time selection.
                </div>
                <div class="border rounded p-2 mb-3">
                    <p v-html="from"></p>
                    <p class="mb-0" v-html="to"></p>
                </div>
                <p v-if="data.confirmation_sent" class="mb-0">
                    Proceed with sending confirmation?
                </p>
                <p v-else class="mb-0">Confirm to proceed with changes?</p>
            </div>
            <confirmation
                v-if="page === 'confirmation'"
                :data="data"
                :isReschedule="true"
                :show-reminder="false"
            />
            <div slot="modal-footer" class="w-100">
                <b-button
                    v-if="!loading"
                    @click="$refs.clickDrag.hide()"
                    class="float-left"
                    variant="secondary"
                    >Cancel</b-button
                >
                <div class="float-right">
                    <img v-if="loading" src="assets/ajax-loader.gif" />
                    <div v-else>
                        <b-button
                            v-if="
                                page == 'appointment' && data.confirmation_sent
                            "
                            @click="save"
                            variant="primary"
                            >Proceed without confirmation</b-button
                        >
                        &nbsp;
                        <b-button
                            v-if="
                                page == 'appointment' && data.confirmation_sent
                            "
                            @click="confirm"
                            variant="primary"
                            >Proceed</b-button
                        >
                        <b-button
                            v-if="
                                page == 'appointment' && !data.confirmation_sent
                            "
                            @click="save"
                            variant="primary"
                            :disabled="isSameTimeAndResource"
                            >Confirm</b-button
                        >
                        <b-button
                            v-if="page == 'confirmation'"
                            @click="save"
                            variant="primary"
                            >Proceed</b-button
                        >
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>
<script>
import CensusMixin from "@/mixins/census";
import ScheduleHelper from "@/module/schedule/helper";
import Confirmation from "./create-update/confirmation.vue";

export default {
    components: { Confirmation },
    mixins: [ScheduleHelper, CensusMixin],
    props: {
        instanceId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            title: "Reschedule Appointment",
            appointment_data: {},
            appointment_event: {
                event: {},
                newResource: {},
                oldEvent: {},
                oldResource: {},
                revert: () => {}
            },
            data: {
                client: {},
                patient: {},
                confirmation_option: "",
                confirmation_sent: true,
                appointmentType: {},
                from: null,
                to: null
            },
            tempData: {},
            page: "appointment",
            loading: false
        };
    },
    computed: {
        mainDescription() {
            if (this.data.client) {
                let msg = "It looks like you are moving the appointment for ";

                if (this.data.client) {
                    msg = `${msg} <strong>${this.data.client.label}</strong>`;
                    msg = this.data.patient
                        ? `${msg} - <strong>${this.data.patient.name}</strong>`
                        : msg;

                    return `${msg},`;
                }

                return `${msg} ${this.data.client.label}?`;
            }

            return "It looks like you are moving the appointment,";
        },
        isSameTimeAndResource() {
            // Check if both time and resource are the same
            const sameTime =
                this.data.from &&
                this.data.to &&
                this.data.from.getTime() === this.data.to.getTime();

            const sameResource =
                this.appointment_event.oldResource &&
                this.appointment_event.newResource &&
                this.appointment_event.oldResource.id ===
                    this.appointment_event.newResource.id;

            // If there's no resource change, just check time
            const noResourceChange =
                !this.appointment_event.oldResource &&
                !this.appointment_event.newResource;

            return sameTime && (sameResource || noResourceChange);
        },
        from() {
            if (!this.appointment_event.oldEvent.start) return null;

            const startTime = new Date(this.appointment_event.oldEvent.start);
            const time = startTime.toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
                timeZone: this._timeZone
            });

            if (this.appointment_event.oldResource)
                return `From: <strong>${this.appointment_event.oldResource.title}</strong> at <strong>${time}</strong>`;

            return `From: <strong>${time}</strong>`;
        },
        to() {
            if (!this.appointment_data.start) return null;

            const endTime = new Date(this.appointment_data.start);
            const time = endTime.toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
                timeZone: this._timeZone
            });

            if (this.appointment_event.newResource)
                return `To: <strong>${this.appointment_event.newResource.title}</strong> at <strong>${time}</strong>`;

            return `To: <strong>${time}</strong>`;
        }
    },
    methods: {
        async open(event) {
            let self = this;

            self.appointment_event = event;
            self.appointment_data = event.event;

            await self._getAppointment(event.event.id).then(response => {
                let staff = "";
                if (self.appointment_event.newResource) {
                    staff = self.appointment_event.newResource.title;
                } else if (response.nonStaff != null) {
                    staff = response.nonStaff.display_name;
                } else if (response.staffColumn != null) {
                    staff = response.staffColumn;
                }

                self.data = {
                    client: response.client,
                    patient: response.patient,
                    confirmation_option: response.confirmation_option,
                    confirmation_sent: response.confirmation_sent,
                    appointmentType: response.appointmentType,
                    staff: { providerName: staff }
                };

                if (self.appointment_event.oldEvent.start) {
                    self.data.from = new Date(
                        self.appointment_event.oldEvent.start
                    );
                }

                if (self.appointment_data.start) {
                    self.data.to = new Date(self.appointment_data.start);
                }
            });

            self.$refs.clickDrag.show();
        },
        close() {
            this.$emit("cancel");
            this.$socket.client.emit("v-broadcast-all", { action: "cancel" });
        },
        confirm() {
            // if confirmation_sent is true, open confirmation dialog
            if (this.page == "appointment" && this.data.confirmation_sent) {
                this.page = "confirmation";
                this.title = "Reschedule Confirmation";
                this.data = {
                    ...this.data,
                    start: this._toDateTime(this.appointment_data.start),

                    // hide reminder field
                    hideReminder: true
                };

                return;
            }
        },
        save() {
            let self = this;

            self.loading = true;

            let post = {
                duration:
                    moment(self.appointment_data.end).unix() -
                    moment(self.appointment_data.start).unix(),
                start: moment(self.appointment_data.start).unix(),
                timestamped: moment().unix()
            };

            if (post.duration <= 0) {
                self.$refs.clickDrag.hide(true);
                self.$emit("cancel");
                self._message(
                    "Appointment failed to save. This may be due to daylight saving time adjustments or an invalid time selection",
                    "warning"
                );
                self.loading = false;
                return;
            }

            if (self.page == "confirmation") {
                post.confirmation = {
                    confirmation_message: self.data.confirmation_message,
                    confirmation_method: self.data.confirmation_option,
                    confirmation_subject: ""
                };
            }

            if (self.appointment_event.newResource) {
                post.resource_id = self.appointment_event.newResource.id;
                post.resource_type =
                    self.appointment_event.newResource.extendedProps.type;
            }

            return self.$http
                .put(
                    process.env.SCHEDULE_SERVICE +
                        "appointment/update-date-time/" +
                        self.appointment_data.id,
                    post
                )
                .then(response => {
                    if (response.data.response.message.code === 200) {
                        this.$parent.refetch(
                            undefined,
                            self.appointment_data.id
                        );

                        self.$refs.clickDrag.hide(true);
                        self._message("Appointment successfully saved");
                    } else {
                        self.$emit("cancel");
                        self._message("Appointment failed to save", "danger");
                    }

                    self.loading = false;
                })
                .catch(function () {
                    self.$emit("cancel");
                    self._message("Appointment failed to save", "danger");

                    self.loading = false;
                });
        }
    }
};
</script>
